"use client"

import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/use-auth"
import { db } from "@/lib/firebase"
import { collection, query, where, getDocs, orderBy } from "firebase/firestore"
import {
  BookOpen, PlayCircle, Download, BarChart3, Clock,
  Video, ChevronRight, TrendingUp, FileText, Loader2,
  CheckCircle2, CreditCard, ShoppingBag
} from "lucide-react"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"

interface Enrollment {
  id: string
  courseId: string
  courseTitle: string
  courseFees: number
  courseType: "free" | "paid"
  courseImageUrl?: string
  instructor?: string
  duration?: string
  enrolledAt: any
  status: string
  paymentId?: string
  paymentAmount?: number
  paymentMethod?: string
  paymentStatus?: string
}

const cardAnim = {
  hidden: { opacity: 0, y: 24 },
  show: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.55, ease: [0.16, 1, 0.3, 1] }
  })
}

export default function DashboardPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState("learning")
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) { setLoading(false); return }
    const fetchEnrollments = async () => {
      try {
        const q = query(
          collection(db, "enrollments"),
          where("userId", "==", user.uid)
        )
        const snap = await getDocs(q)
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Enrollment))
        setEnrollments(data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    fetchEnrollments()
  }, [user])

  const paidEnrollments = enrollments.filter(e => e.courseType === "paid")
  const totalSpent = paidEnrollments.reduce((acc, e) => acc + (e.paymentAmount || 0), 0)

  return (
    <div className="min-h-screen bg-slate-50 pt-16 sm:pt-24 pb-20 sm:pb-32">

      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-8 sm:mb-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-slate-200 pb-6 sm:pb-8">
          <div>
            <p className="text-[#b48c36] font-bold text-[8px] sm:text-[10px] uppercase tracking-[0.4em] mb-2 sm:mb-3">Scholar Portal</p>
            <h1 className="text-2xl sm:text-5xl font-serif font-medium text-slate-950 tracking-tight">
              Welcome back,{" "}
              <span className=" font-light">
                {user?.displayName?.split(" ")[0] || "Scholar"}.
              </span>
            </h1>
          </div>
          {/* Live stat badges */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="bg-white border border-slate-200 px-5 py-3 text-center">
              <p className="text-2xl font-serif font-bold text-slate-950">{enrollments.length}</p>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Enrolled Courses</p>
            </div>
            <div className="bg-slate-950 px-5 py-3 text-center">
              <p className="text-2xl font-serif font-bold text-[#b48c36]">₹{totalSpent.toLocaleString()}</p>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Total Invested</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid grid-cols-1 xl:grid-cols-12 gap-8 sm:gap-10">

        {/* Sidebar Nav */}
        <div className="xl:col-span-3 flex flex-row xl:flex-col overflow-x-auto gap-2 border-b xl:border-b-0 pb-4 xl:pb-0">
          {[
            { id: "learning", Icon: BookOpen, label: "My Classrooms" },
            { id: "payments", Icon: CreditCard, label: "Payment History" },
            { id: "live",     Icon: Video,     label: "Live Sessions" },
            { id: "downloads",Icon: Download,  label: "Library & Notes" },
          ].map(({ id, Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-4 px-6 py-4 border text-left min-w-[200px] xl:min-w-0 transition-all ${
                activeTab === id
                  ? "bg-white border-slate-200 shadow-sm text-[#b48c36]"
                  : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-white/50"
              }`}
            >
              <Icon size={18} />
              <span className="font-serif text-lg font-medium">{label}</span>
            </button>
          ))}
        </div>

        {/* Main content */}
        <div className="xl:col-span-9 space-y-10">

          {/* ── My Classrooms ── */}
          {activeTab === "learning" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-serif font-medium text-slate-950">Active Enrollments</h2>
              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="animate-spin text-[#b48c36]" size={32} />
                </div>
              ) : enrollments.length === 0 ? (
                <div className="bg-white border border-slate-200 p-12 text-center space-y-4">
                  <ShoppingBag size={36} className="text-slate-200 mx-auto" />
                  <p className="text-slate-500 text-sm font-serif ">You haven't enrolled in any course yet.</p>
                  <Link
                    to="/#courses"
                    className="inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-colors"
                  >
                    Browse Courses <ChevronRight size={14} />
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {enrollments.map((enrollment, i) => (
                    <motion.div
                      key={enrollment.id}
                      custom={i}
                      variants={cardAnim}
                      initial="hidden"
                      animate="show"
                      className="bg-white border border-slate-200 overflow-hidden hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.07)] transition-all group"
                    >
                      {/* Thumbnail */}
                      {enrollment.courseImageUrl && (
                        <div className="h-36 overflow-hidden bg-slate-100">
                          <img
                            src={enrollment.courseImageUrl}
                            alt={enrollment.courseTitle}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                      )}
                      <div className="p-6 flex flex-col gap-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-[8px] font-bold uppercase tracking-widest px-2 py-1 ${
                              enrollment.courseType === "free"
                                ? "bg-emerald-50 text-emerald-600"
                                : "bg-[#b48c36]/10 text-[#b48c36]"
                            }`}>
                              {enrollment.courseType === "free" ? "Free Access" : `₹${(enrollment.paymentAmount || 0).toLocaleString()} Paid`}
                            </span>
                            <CheckCircle2 size={16} className="text-emerald-500" />
                          </div>
                          <h3 className="text-lg font-serif font-medium text-slate-900 leading-snug">
                            {enrollment.courseTitle}
                          </h3>
                          {enrollment.instructor && (
                            <p className="text-xs text-slate-400 mt-1">By {enrollment.instructor}</p>
                          )}
                        </div>
                        <Link
                          to={`/courses/${enrollment.courseId}`}
                          className="flex items-center justify-between bg-slate-950 hover:bg-[#b48c36] text-white px-4 py-3 text-[9px] font-bold uppercase tracking-widest transition-colors"
                        >
                          Continue Learning <ChevronRight size={14} />
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Payment History ── */}
          {activeTab === "payments" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-serif font-medium text-slate-950">Payment History</h2>
              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="animate-spin text-[#b48c36]" size={32} />
                </div>
              ) : paidEnrollments.length === 0 ? (
                <div className="bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm font-serif ">
                  No paid transactions yet.
                </div>
              ) : (
                <div className="bg-white border border-slate-200 overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-100 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                    <span>Course</span>
                    <span className="text-right">Amount</span>
                    <span className="text-right">Status</span>
                  </div>
                  {paidEnrollments.map((e, i) => (
                    <div
                      key={e.id}
                      className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-5 border-b border-slate-50 hover:bg-slate-50 transition-colors items-center"
                    >
                      <div className="min-w-0">
                        <p className="font-serif font-semibold text-slate-900 text-sm truncate">{e.courseTitle}</p>
                        {e.paymentId && (
                          <p className="text-[9px] text-slate-400 font-mono mt-0.5 truncate">ID: {e.paymentId}</p>
                        )}
                      </div>
                      <span className="text-sm font-bold text-slate-950 font-serif whitespace-nowrap">
                        ₹{(e.paymentAmount || 0).toLocaleString()}
                      </span>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 whitespace-nowrap">
                        {e.paymentStatus === "captured" ? "Paid" : e.paymentStatus || "—"}
                      </span>
                    </div>
                  ))}
                  {/* Total row */}
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-4 bg-slate-950 items-center">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Invested</span>
                    <span className="text-lg font-serif font-bold text-[#b48c36]">₹{totalSpent.toLocaleString()}</span>
                    <span />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Live Sessions ── */}
          {activeTab === "live" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-serif font-medium text-slate-950">Upcoming Live Sessions</h2>
              <div className="bg-white border border-slate-200 divide-y divide-slate-100">
                <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div className="flex gap-6 items-start">
                    <div className="hidden sm:flex flex-col items-center justify-center p-4 bg-red-50 text-red-600 rounded-sm border border-red-100 min-w-[80px]">
                      <span className="text-[10px] font-bold uppercase tracking-widest">Today</span>
                      <span className="text-xl font-serif font-bold">14:00</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" /> Starting Soon
                      </p>
                      <h4 className="text-xl font-serif font-medium text-slate-900">Current Affairs: Economic Survey Analysis</h4>
                      <p className="text-sm text-slate-500 mt-2">Join live to ask doubts directly to the faculty.</p>
                    </div>
                  </div>
                  <button className="bg-slate-950 text-white px-6 py-3 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-colors w-full sm:w-auto">
                    Join Stream
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Downloads ── */}
          {activeTab === "downloads" && (
            <div className="space-y-6">
              <h2 className="text-2xl font-serif font-medium text-slate-950">Library & PDF Notes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: "Fundamental Rights Complete Handout", size: "2.4 MB" },
                  { title: "Economic Survey Chapter 1-3 Summary", size: "1.1 MB" },
                  { title: "Telangana History Chronology Map", size: "4.5 MB" },
                  { title: "Governance Sub-Module Notes", size: "800 KB" },
                ].map((doc, i) => (
                  <div key={i} className="flex items-center justify-between p-5 bg-white border border-slate-200 hover:border-[#b48c36]/40 transition-colors group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-50 flex items-center justify-center border border-slate-100 group-hover:bg-[#b48c36]/5 group-hover:border-[#b48c36]/20 transition-colors">
                        <FileText size={16} className="text-slate-400 group-hover:text-[#b48c36]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900 group-hover:text-[#b48c36] transition-colors">{doc.title}</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{doc.size} • PDF</p>
                      </div>
                    </div>
                    <button className="text-slate-300 hover:text-[#b48c36] transition-colors">
                      <Download size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
